import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { getCustomerOrderInvoice } from '../../api/apiStorefront';
import parseError from '../../utils/parseError';

/**
 * Fetches the customer's order invoice as a PDF blob and triggers a
 * browser download. The bearer header can't ride on window.open(),
 * so we go through the authenticated axios client and a temporary
 * object URL — same pattern as the dashboard's useGetOrderInvoice.
 *
 *   const { download, isFetching } = useGetCustomerOrderInvoice(orderId);
 *   download('ar');
 */
export const useGetCustomerOrderInvoice = (orderId) => {
    const [isFetching, setIsFetching] = useState(false);
    const { t } = useTranslation();

    const download = useCallback(
        async (lang) => {
            if (!orderId) return;
            setIsFetching(true);
            try {
                const res = await getCustomerOrderInvoice(
                    orderId,
                    lang ? { lang } : undefined
                );
                const blob = new Blob([res.data], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);

                const a = document.createElement('a');
                a.href = url;
                a.download = `invoice-${orderId}${lang ? `-${lang}` : ''}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);

                // Revoke after a minute so the file has time to land.
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
            } catch (error) {
                toast.error(parseError(error) || t('orders.invoiceFailed', 'Failed to load invoice'));
            } finally {
                setIsFetching(false);
            }
        },
        [orderId, t]
    );

    return { download, isFetching };
};

export default useGetCustomerOrderInvoice;
